/**
 * Client calls for canned responses (work order SUPPORT-TOOLS item 5), on the shared
 * {@link opsClient}. Cross-shop, so no shop grant: a snippet is not one shop's data.
 *
 * Every write carries the operator's reason and answers with the `OperatorAuditLog` row id, which
 * the page shows — the audit trail is the control, there being no role model (D11).
 */

import axios from 'axios';
import { ListCannedResponsesCategoryEnum } from '../../../../libs/api-client/src/generated/api/internal-admin-api';
import {
  CreateCannedResponseDtoCategoryEnum,
  UpdateCannedResponseDtoCategoryEnum,
  type CannedResponseListDto,
  type CannedResponseWriteResultDto,
} from '../../../../libs/api-client/src/generated/models';
import {
  narrowTo,
  type SnippetField,
  type SnippetForm,
} from './canned-responses';
import { opsClient } from './ops-api';

// Re-exported from the generated client rather than restated here — see `support-api.ts`.
export type {
  CannedResponseDto as CannedResponse,
  CannedResponseListDto as CannedResponseList,
  CannedResponseWriteResultDto as CannedResponseWriteResult,
} from '../../../../libs/api-client/src/generated/models';

/** Every ticket category, read off the generated client for the dropdowns. */
export const CANNED_RESPONSE_CATEGORIES = Object.values(
  ListCannedResponsesCategoryEnum,
);

/** Filters for the list. `category` is the picker's (category + "any"); see `filterByCategory`. */
export interface CannedResponseFilters {
  readonly includeArchived?: boolean;
  readonly category?: string;
  readonly q?: string;
}

/**
 * The snippet list. The API synchronises the shipped defaults first, so the first visit after a
 * release already shows the new ones.
 */
export async function listCannedResponses(
  filters: CannedResponseFilters,
): Promise<CannedResponseListDto> {
  const category = filters.category
    ? narrowTo(CANNED_RESPONSE_CATEGORIES, filters.category)
    : undefined;
  const q = filters.q?.trim();
  const { data } = await opsClient().listCannedResponses({
    ...(filters.includeArchived ? { includeArchived: true } : {}),
    ...(category ? { category } : {}),
    ...(q ? { q } : {}),
  });
  return data;
}

/** Writes a new snippet. */
export async function createCannedResponse(
  form: SnippetForm,
  reason: string,
): Promise<CannedResponseWriteResultDto> {
  const { data } = await opsClient().createCannedResponse({
    createCannedResponseDto: {
      reason: reason.trim(),
      title: form.title.trim(),
      bodyDe: form.bodyDe,
      bodyEn: form.bodyEn,
      category:
        narrowTo(
          Object.values(CreateCannedResponseDtoCategoryEnum),
          form.category,
        ) ?? null,
    },
  });
  return data;
}

/**
 * Saves the changed fields of a snippet against the version they were made on.
 *
 * Sends ONLY the changed fields: an unchanged body that another operator edited meanwhile would
 * otherwise be written back over theirs the moment the version check is satisfied.
 *
 * @param id - The snippet.
 * @param form - The editor's contents.
 * @param fields - From `changedFields`.
 * @param expectedUpdatedAt - The `updatedAt` of the version edited. A newer one answers 409.
 * @param reason - The operator's reason.
 */
export async function updateCannedResponse(
  id: string,
  form: SnippetForm,
  fields: readonly SnippetField[],
  expectedUpdatedAt: string,
  reason: string,
): Promise<CannedResponseWriteResultDto> {
  const { data } = await opsClient().updateCannedResponse({
    id,
    updateCannedResponseDto: {
      reason: reason.trim(),
      expectedUpdatedAt,
      ...(fields.includes('title') ? { title: form.title.trim() } : {}),
      ...(fields.includes('bodyDe') ? { bodyDe: form.bodyDe } : {}),
      ...(fields.includes('bodyEn') ? { bodyEn: form.bodyEn } : {}),
      ...(fields.includes('category')
        ? {
            category:
              narrowTo(
                Object.values(UpdateCannedResponseDtoCategoryEnum),
                form.category,
              ) ?? null,
          }
        : {}),
    },
  });
  return data;
}

/** Hides a snippet from the picker. */
export async function archiveCannedResponse(
  id: string,
  reason: string,
): Promise<CannedResponseWriteResultDto> {
  const { data } = await opsClient().archiveCannedResponse({
    id,
    operatorReasonDto: { reason: reason.trim() },
  });
  return data;
}

/** Puts an archived snippet back into the picker. */
export async function restoreCannedResponse(
  id: string,
  reason: string,
): Promise<CannedResponseWriteResultDto> {
  const { data } = await opsClient().restoreCannedResponse({
    id,
    operatorReasonDto: { reason: reason.trim() },
  });
  return data;
}

/**
 * Whether a write was refused because the snippet changed under the operator (409) — another
 * operator's save, or an archive/restore that already happened. The page answers it by reloading
 * and showing the current version, never by retrying.
 */
export function isCannedResponseConflict(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}
